import React, { useState, useContext } from 'react';
import { UserContext } from '../../../context/UserContext';
import { useFamily } from '../../../context/FamilyContext';
import MyButton from '../../basic/MyButton';
import MySpinner from '../../basic/MySpinner';
import FamilyMemberCard from './FamilyMemberCard';
import AddFamilyMemberModal from './ModalAddFamilyMember';
import EditFamilyMemberModal from './ModalEditFamilyMember';
import MyConfirmationModal from '../../basic/MyConfirmationModal';
import Notification from '../../Notification/Notification';
import { addUser, deleteUser, updateChildAllowanceSettings, depositFunds } from '../../../api';
import './ManageFamilyView.css';

function ManageFamilyView() {
    const { user } = useContext(UserContext);
    const { familyChildren, familyParents, refetchFamilyData, loading } = useFamily();
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [notification, setNotification] = useState({ message: '', type: '', show: false });

    const showNotification = (message, type) => {
        setNotification({ message, type, show: true });
    };

    const handleAddMember = async (newMemberData) => {
        try {
            console.log("Adding new member with data:", newMemberData);
            
            const newUser = await addUser(newMemberData);
            console.log("New user created:", newUser);

            if (!newUser || !newUser.user_id) {
                throw new Error("Failed to create user: No user ID returned");
            }

            if (newMemberData.role === 'child') {
                console.log("Setting allowance for child:", newUser.user_id, newMemberData.allowanceSettings);
                
                const formattedAllowanceSettings = {
                    amount: parseFloat(newMemberData.allowanceSettings.amount),
                    day_of_distribution: newMemberData.allowanceSettings.day,
                    frequency: 'Weekly'
                };

                await updateChildAllowanceSettings(newUser.user_id, formattedAllowanceSettings);
                
                console.log("Making initial deposit for child:", newUser.user_id, newMemberData.initialDeposit);
                await depositFunds(newUser.user_id, newMemberData.initialDeposit.amount, newMemberData.initialDeposit.reason);
            }

            setIsAddModalOpen(false);
            refetchFamilyData();
            showNotification("Family member added successfully", "success");
        } catch (err) {
            console.error("Failed to add family member:", err);
            showNotification(`Failed to add family member: ${err.message}`, "error");
        }
    };

    const handleEditMember = (member) => {
        const memberId = member.parentId || member.childId;
        setSelectedMember(memberId);
        setIsEditModalOpen(true);
    };

    const handleDeleteMember = (member) => {
        setSelectedMember(member);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedMember) {
            const memberId = selectedMember.parentId || selectedMember.childId;

            if (!memberId) {
                console.error("Invalid member ID");
                showNotification("Failed to delete family member: Invalid ID", "error");
                return;
            }

            try {
                await deleteUser(memberId);
                setIsDeleteModalOpen(false);
                refetchFamilyData();
                showNotification(`${selectedMember.display_name} has been removed from the family`, "success");
            } catch (err) {
                console.error("Failed to delete family member:", err);
                showNotification("Failed to delete family member. Please try again.", "error");
            }
        }
    };

    if (loading) {
        return (
            <div className="loading-container">
                <MySpinner size="large" color="#0ACF83" />
            </div>
        );
    }

    const noFamilyMembers = (!familyParents || familyParents.length === 0) && (!familyChildren || familyChildren.length === 0);

    return (
        <div className="manage-family-view">
            <div className="manage-family-frame">
                <h1>Manage Family</h1>
                {notification.show && 
                    <Notification 
                        message={notification.message} 
                        type={notification.type} 
                        onClose={() => setNotification({ ...notification, show: false })} 
                    />
                }

                {noFamilyMembers ? (
                    <div className="empty-family-message">
                        <p>Your family is empty. Start by adding a family member!</p>
                    </div>
                ) : (
                    <>
                        <section className="family-section parents-section">
                            <h2>Parents / Guardians</h2>
                            {familyParents && familyParents.length > 0 ? (
                                familyParents.map(parent => (
                                    <FamilyMemberCard
                                        key={parent.parentId}
                                        member={{...parent, role: 'parent'}}
                                        onEdit={() => handleEditMember(parent)}
                                        onDelete={() => handleDeleteMember(parent)}
                                    />
                                ))
                            ) : (
                                <p>No parents or guardians added yet.</p>
                            )}
                        </section>

                        <div className="family-section-delimiter"></div>

                        <section className="family-section children-section">
                            <h2>Children</h2>
                            {familyChildren && familyChildren.length > 0 ? (
                                familyChildren.map(child => (
                                    <FamilyMemberCard
                                        key={child.childId}
                                        member={{...child, role: 'child'}}
                                        onEdit={() => handleEditMember(child)}
                                        onDelete={() => handleDeleteMember(child)}
                                    />
                                ))
                            ) : (
                                <p>No children added yet.</p>
                            )}
                        </section>
                    </>
                )}

                <MyButton text="Add Family Member" onClick={() => setIsAddModalOpen(true)} />
            </div>

            <AddFamilyMemberModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onAddMember={handleAddMember}
            />

            <EditFamilyMemberModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                memberId={selectedMember}
                onEditSuccess={(message) => {
                    refetchFamilyData();
                    showNotification(message, "success");
                    setIsEditModalOpen(false);
                }}
            />

            <MyConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Family Member"
                message={`Are you sure you want to remove ${selectedMember?.display_name} from the family? This action cannot be undone.`}
            />
        </div>
    );
}

export default ManageFamilyView;